<script setup lang="ts">
import LangPicker from './LangPicker.vue';
import ManualLookupForm from './ManualLookupForm.vue';
import { useTheme } from '../composables/useTheme';

defineProps<{
  denied: boolean;
  lookupBusy: boolean;
  lookupError: string | null;
  hasHistory: boolean;
  hasFavorites: boolean;
}>();
defineEmits<{
  enable: [];
  lookup: [plate: string, region?: string];
  history: [];
  favorites: [];
}>();

const { theme, toggleTheme } = useTheme();
</script>

<template>
  <div class="sheet center">
    <img class="brand-logo" src="/brand/autospy-logo.png" alt="AutoSpy" />
    <p class="tagline">{{ $t('priming.tagline') }}</p>
    <p class="muted">
      {{ $t('priming.blurb') }}
      <a href="/privacy.html">{{ $t('priming.privacy') }}</a>
    </p>
    <ManualLookupForm
      :busy="lookupBusy"
      :error="lookupError"
      @lookup="(p: string, r?: string) => $emit('lookup', p, r)"
    />
    <p v-if="denied" class="warn">{{ $t('priming.denied') }}</p>
    <div class="priming-actions">
      <button class="btn primary" @click="$emit('enable')">
        {{ denied ? $t('priming.retry') : $t('priming.enable') }}
      </button>
      <button
        v-if="hasHistory"
        class="btn square"
        :title="$t('history.title')"
        :aria-label="$t('history.title')"
        @click="$emit('history')"
      >
        ≡
      </button>
      <button
        v-if="hasFavorites"
        class="btn square"
        :title="$t('history.favorites')"
        :aria-label="$t('history.favorites')"
        @click="$emit('favorites')"
      >
        ★
      </button>
    </div>
    <div class="priming-footer">
      <LangPicker />
      <button
        class="theme-toggle"
        :title="$t('aria.theme')"
        :aria-label="$t('aria.theme')"
        @click="toggleTheme"
      >
        <!-- Shows the mode a tap switches to: sun while dark, moon while light. -->
        <svg
          v-if="theme === 'dark'"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        >
          <circle cx="12" cy="12" r="4.5" />
          <path
            d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8"
          />
        </svg>
        <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11z" />
        </svg>
      </button>
    </div>
  </div>
</template>
